import {
  Checkbox,
  KoDate,
  KoForm,
  KoFormBuilder,
  KoSelect,
  KoTextArea,
  KoUserSelect
} from 'packages';
import { isValid, format } from 'date-fns';
import { useCallback, useEffect } from 'react';
import { useSelector } from "react-redux";
import styles from './crud.module.scss';
import ItemEntryCrud from './itemEntryCrud';
import { generateAllTimes, generateEndTimes } from 'shared/utils';

// // Constants
// const optionGroups = [
//   { label: 'Zoom', value: "zoom" },
//   { label: 'Microsoft Teams', value: "teams" },
//   { label: 'Google Meet', value: "gmeet" }
//   // { label: 'Slack', value: "slack" },
//   // { label: 'Skype', value: "skype" },
//   // { label: 'GoToMeeting', value: "goToMeeting" },
//   // { label: 'Cisco Webex', value: "ciscoWebex" }
// ];

const RecurrenceValues = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekdays Only (Mon–Fri)', value: 'weekday' },
  { label: 'Weekly (Same Day)', value: 'weekly' },
  { label: 'Weekends Only (Sat–Sun)', value: 'weekends' },
  { label: 'Monthly (Same Date)', value: 'monthly' }
];

const OneValues = [
  { label: '1-on-1 Meeting', value: '1' },
  { label: 'Group Meeting', value: '2' }
];

const permissionOptions = [
  { label: 'Modify Events', value: 'modify_events' },
  { label: 'Invite Others', value: 'invite_others' },
  { label: 'Attendees List', value: 'attendees_list' }
];

function EventCrud({ form, handleFinish, initialValues }) {
  const users = useSelector(state => state?.users?.data || []);
  const groups = useSelector(state => state?.groups?.data || []);

  const eventTypeWatch = KoForm.useWatch('eventType', form);
  const startTimeWatch = KoForm.useWatch('startTime', form);


  useEffect(() => {
    const hasValues = initialValues && Object.keys(initialValues).length > 0;

    if (hasValues) {
      const current = form.getFieldsValue();

      const normalizeTime = (time) => {
        if (!time) return undefined;
        const dateObj = typeof time === 'string' ? new Date(time) : time;
        return isValid(dateObj) ? format(dateObj, 'HH:mm') : time;
      };

      const normalizedValues = {
        ...initialValues,
        startTime: normalizeTime(initialValues.startTime),
        endTime: normalizeTime(initialValues.endTime),
        assignees: Array.isArray(initialValues.assignees)
          ? initialValues.assignees.map((a) =>
            typeof a === 'string' ? a : a.userId
          )
          : [],

        groups: Array.isArray(initialValues.groups)
          ? initialValues.groups.map((a) =>
            typeof a === 'string' ? a : a.groupId
          )
          : []
       
      };

      const changed = Object.entries(normalizedValues).some(
        ([key, val]) => current[key] !== val
      );

      if (changed) {
        form.setFieldsValue(normalizedValues);
      }
    } else {
      form.resetFields();
    }
  }, [form, JSON.stringify(initialValues)]);




  const handleSubmit = useCallback((values) => {
    handleFinish(values);
  }, [handleFinish]);

  // Dynamic Components
  const Email = ({ value, onChange }) => (
    <KoSelect mode="tags" tokenSeparators={[',']} value={value} onChange={onChange} placeholder='Enter Email' />
  );

  const Assignees = ({ value, onChange }) => (
    <KoUserSelect users={users} value={value} onChange={onChange} placeholder='Select assignees' mode="multiple" />
  );

  const Groups = ({ value, onChange }) => (
    <KoSelect
      mode="multiple"
      showSearch
      allowClear
      value={value}
      onChange={onChange}
      placeholder='Choose groups'
      options={groups.map(group => ({ label: group.name, value: group.groupId }))}
    />
  );

  const StartDate = ({ value, onChange }) => (
    <KoDate
      value={value}
      format={'DD-MMM-YYYY'}
      onChange={onChange}
    />
  );

  const allTimes = generateAllTimes();
  const StarTime = ({ value, onChange }) => {
    return (
      <KoSelect
        value={value}
        onChange={onChange}
        showSearch
        allowClear
        placeholder="Choose Start Time"
        options={allTimes}
      />
    );
  };


  const EndTime = ({ value, onChange }) => {
    return (
      <KoSelect
        value={value}
        onChange={onChange}
        showSearch
        allowClear
        placeholder="Choose End Time"
        disabled={!startTimeWatch}
        options={generateEndTimes(startTimeWatch)}
      />
    );
  };


  // const EndDate = ({ value, onChange }) => (
  //   <KoDateTime
  //     value={value}
  //     format={'DD-MMM-YYYY HH:mm'}
  //     onChange={v => onChange(v)}
  //   />
  // );

  const Recurrence = ({ value, onChange }) => (
    <KoSelect
      value={value}
      onChange={onChange}
      showSearch
      allowClear
      placeholder='Choose recurrence'
      options={RecurrenceValues}
    />
  );

  const EventType = ({ value, onChange }) => (
    <KoSelect
      value={value}
      onChange={onChange}
      showSearch
      allowClear
      placeholder='Choose Engagement Type'
      options={OneValues}
    />
  );


  // const EventVia = ({ value, onChange }) => {
  //   return (
  //     <KoSelect
  //       allowClear
  //       showSearch
  //       value={value}
  //       onChange={(v) => onChange(v)}
  //       className={styles.dropdown}
  //       placeholder="Choose Meeting via"
  //       options={optionGroups}
  //       optionRender={(option) => (
  //         <div className={styles.iconWithLabel} role="img" aria-label={option.data.label}>
  //           {option.data.value === 'zoom' && <KoIcon name="ZoomFilled" />}
  //           {option.data.value === 'teams' && <KoIcon name="MicrosoftTeamsFilled" />}
  //           {option.data.value === 'gmeet' && <KoIcon name="GoogleMeetFilled" />}
  //           {option.data.label}
  //         </div>
  //       )}
  //     />
  //   );
  // };


  const TextArea = ({ value, onChange }) => (
    <KoTextArea value={value} onChange={onChange} showCount
      maxLength={100}/>
  );

  // Form Meta
  const fields = [
    { key: 'name', label: 'Event Name', colSpan: 6, rules: [{ required: true, message: 'Enter Event Name.' }] },
    { key: 'groups', label: 'Groups', colSpan: 2, widget: Groups },
    { key: 'assignees', label: 'Assignees', colSpan: 2, widget: Assignees },
    { key: 'email', label: 'Custom Email', colSpan: 2, widget: Email },
    { key: 'startDate', label: 'Start Date', colSpan: 2, widget: StartDate, rules: [{ required: true }] },
    { key: 'startTime', label: 'Start Time', colSpan: 2, widget: StarTime, rules: [{ required: true }] },
    { key: 'endTime', label: 'End Time', colSpan: 2, widget: EndTime, rules: [{ required: true }] },
    // { key: 'meetingVia', label: 'Meeting via', colSpan: 2, widget: EventVia },
    { key: 'reoccurrence', label: 'Recurrence', colSpan: 2, widget: Recurrence },
    { key: 'eventType', label: 'Engagement Type', colSpan: 2, widget: EventType },
    eventTypeWatch === '2' && { key: 'membersCount', label: 'How many members', colSpan: 2, type: 'number' },
    { key: 'description', label: 'Instruction / Notes', colSpan: 6, widget: TextArea },
    { key: 'permissions', label: 'Attendees Permission', colSpan: 4, widget: Checkbox.Group, options: permissionOptions },
    { key: '', label: 'Agenda', colSpan: 6, widget: () => <ItemEntryCrud form={form} handleFinish={handleFinish} initialValues={initialValues} /> }
  ].filter(Boolean); // remove false items

  const meta = {
    columns: 6,
    formItemLayout: null,
    initialValues,
    colon: true,
    fields
  };

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.InvoiceCrudwrapper}>
        <KoForm form={form} layout="vertical" onFinish={handleSubmit}>
          <KoFormBuilder form={form} meta={meta} />
        </KoForm>
      </div>
    </div>
  );
}

export default EventCrud;
